import type { ReactNode } from 'react';
import { Alert, Divider, Group, Stack, Table, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { estimateValueCents, formatQty, formatRands } from '../format';
import { avgCostByProduct, useStockOnHand } from '../api';
import { LocationName, ProductName } from './EntityNames';
import type { LineFormRow } from './LineRowsEditor';

interface LedgerPreviewProps {
  siteId: string | undefined;
  lines: LineFormRow[];
  /** Line under the total, e.g. the cost centre name for an issue, or same-site/
   * cross-site for a transfer. Accepts a node so callers can compose it from a
   * resolved-name component (see EntityNames.tsx) rather than a plain string. */
  attribution?: ReactNode;
}

/** "Review with a ledger-effect preview" (BUILD_SPEC.md §8.6, §11: "every posting
 * screen shows a plain-language preview of ledger effects before Post"). Values here
 * are ESTIMATES computed client-side from the product×site pool's current WAC
 * average (`GET /reporting/stock-on-hand`) — the authoritative figure is only known
 * once `recordMovement` actually runs at Post (CLAUDE.md: avg cost is always
 * derived, never stored, and can move between preview and Post if something else
 * posts to the same pool first). That's why every number here is labelled
 * "estimated" rather than presented as the final figure. */
export function LedgerPreview({ siteId, lines, attribution }: LedgerPreviewProps) {
  const { data, isLoading } = useStockOnHand(siteId);
  const avgCosts = avgCostByProduct(data?.items);

  const validLines = lines.filter((line) => line.productId && line.qty);
  const rows = validLines.map((line) => {
    const avg = avgCosts.get(line.productId);
    const estCents = avg ? estimateValueCents(avg, line.qty) : null;
    return { ...line, avg, estCents };
  });
  const totalCents = rows.reduce((sum, r) => sum + (r.estCents ?? 0), 0);
  const anyUnknown = rows.some((r) => r.estCents === null);
  const showLocationColumn = rows.some((r) => r.locationId);

  return (
    <Stack gap="sm">
      <Alert icon={<IconInfoCircle size={18} />} color="graphite" variant="light">
        This is an estimate at each product&apos;s current weighted-average cost. The exact value is confirmed when you
        Post.
      </Alert>
      <Table.ScrollContainer minWidth={640}>
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Product</Table.Th>
              {showLocationColumn && <Table.Th>Location</Table.Th>}
              <Table.Th>Qty</Table.Th>
              <Table.Th ta="right">Est. value</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.key}>
                <Table.Td>{row.productLabel || <ProductName id={row.productId} />}</Table.Td>
                {showLocationColumn && (
                  <Table.Td>{row.locationId ? row.locationLabel || <LocationName id={row.locationId} /> : '—'}</Table.Td>
                )}
                <Table.Td>{formatQty(row.qty)}</Table.Td>
                <Table.Td ta="right">{isLoading ? '…' : row.estCents !== null ? formatRands(row.estCents) : 'No prior cost'}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Divider />
      <Group justify="space-between">
        <Text fw={600}>{attribution}</Text>
        <Text fw={700} size="lg">
          Estimated total: {formatRands(totalCents)}
        </Text>
      </Group>
      {anyUnknown && (
        <Text size="sm" c="dimmed">
          Some lines have no prior stock at this site, so their opening value can&apos;t be estimated yet — they will still
          Post correctly (e.g. an opening-balance-style first movement).
        </Text>
      )}
    </Stack>
  );
}
